import React, { useState, useEffect, useCallback } from 'react'
import {
    FileText, Search, Filter, DollarSign, IndianRupee,
    FileDown, RotateCcw, ChevronDown, ChevronRight,
    XCircle, Eye
} from 'lucide-react'
import PageIdentifier from '../shared/PageIdentifier'
import SCREEN_IDS from '../../utils/screenIds'
import * as XLSX from 'xlsx'
import BillDetailModal from '../shared/BillDetailModal'

// ── Helpers ───────────────────────────────────────────────────────────────────

const today = new Date()
const threeMonthsAgo = new Date(today); threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
const defaultStartDate = `${threeMonthsAgo.getFullYear()}-${String(threeMonthsAgo.getMonth() + 1).padStart(2, '0')}-01`
const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

const fmtNum = (n, decimals = 2) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—'
const currSym = (c) => c === 'USD' ? '$' : '₹'

// Status badge (kept local for the table rows)
const StatusBadge = ({ status }) => {
    if (status === 'completed') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
            Completed
        </span>
    )
    if (status === 'cancelled') return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
            Cancelled
        </span>
    )
    return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{status}</span>
    )
}

// ── Cancel Confirm Modal ──────────────────────────────────────────────────────

const CancelModal = ({ bill, onConfirm, onClose, cancelling }) => {
    const [reason, setReason] = useState('')
    if (!bill) return null
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-1">Cancel Bill #{bill.bill_number}?</h2>
                <p className="text-sm text-gray-500 mb-4">
                    This will mark the bill as cancelled and return item <strong>{bill.item_code || 'N/A'}</strong> to "In Stock".
                </p>
                <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Reason for cancellation (required)"
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                />
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} disabled={cancelling}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600">
                        Keep Bill
                    </button>
                    <button
                        onClick={() => onConfirm(reason)}
                        disabled={!reason.trim() || cancelling}
                        className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {cancelling && <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                        Cancel Bill
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Main Component ────────────────────────────────────────────────────────────

const BillingHistory = () => {
    const [bills, setBills] = useState([])
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(0)
    const PAGE_SIZE = 50

    const [filters, setFilters] = useState({
        search: '',
        currency: 'all',
        status: 'all',
        startDate: defaultStartDate,
        endDate: todayStr,
    })
    const [fetchError, setFetchError] = useState(null)

    // Summary totals across current page results
    const [summary, setSummary] = useState({ inr: 0, usd: 0, count: 0 })

    // Expand / detail
    const [expandedRows, setExpandedRows] = useState(new Set())
    const [detailBill, setDetailBill] = useState(null)
    const [cancelTarget, setCancelTarget] = useState(null)
    const [cancelling, setCancelling] = useState(false)

    // ── Fetch ────────────────────────────────────────────────────────────────────
    const fetchBills = useCallback(async () => {
        setLoading(true)
        setFetchError(null)
        try {
            const params = new URLSearchParams()
            if (filters.search) params.set('search', filters.search)
            if (filters.currency && filters.currency !== 'all') params.set('currency', filters.currency)
            if (filters.status && filters.status !== 'all') params.set('status', filters.status)
            else params.set('status', '')
            if (!filters.search && filters.startDate) params.set('startDate', filters.startDate)
            if (!filters.search && filters.endDate) params.set('endDate', filters.endDate + 'T23:59:59')
            params.set('limit', String(PAGE_SIZE))
            params.set('offset', String(page * PAGE_SIZE))

            const res = await fetch(`/api/billings?${params}`, { credentials: 'include' })
            const data = await res.json()

            if (data.success) {
                const rows = data.data || []
                setBills(rows)
                setTotalCount(data.count || rows.length)

                const s = rows.reduce((acc, b) => {
                    if (b.status === 'cancelled') return acc
                    if (b.currency === 'INR') acc.inr += parseFloat(b.grand_total || 0)
                    else acc.usd += parseFloat(b.grand_total || 0)
                    acc.count++
                    return acc
                }, { inr: 0, usd: 0, count: 0 })
                setSummary(s)
            } else {
                setFetchError(data.error || 'Failed to load billing history')
            }
        } catch (err) {
            console.error('Failed to fetch billing history:', err)
            setFetchError(err.message || 'Network error loading billing history')
        } finally {
            setLoading(false)
        }
    }, [filters, page])

    useEffect(() => { fetchBills() }, [fetchBills])

    // Reset to page 0 on filter change
    const handleFilterChange = (key, value) => {
        setPage(0)
        setFilters(prev => ({ ...prev, [key]: value }))
        setExpandedRows(new Set())
    }

    const resetFilters = () => {
        setPage(0)
        setFilters({ search: '', currency: 'all', status: 'all', startDate: defaultStartDate, endDate: todayStr })
        setExpandedRows(new Set())
    }

    // ── Toggle row expansion ─────────────────────────────────────────────────────
    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            if (next.has(id)) { next.delete(id) } else { next.add(id) }
            return next
        })
    }

    // ── Cancel ───────────────────────────────────────────────────────────────────
    const handleCancelConfirm = async (reason) => {
        if (!cancelTarget) return
        setCancelling(true)
        try {
            const res = await fetch(`/api/billings/${cancelTarget.id}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason }),
            })
            const data = await res.json()
            if (data.success) {
                setCancelTarget(null)
                setDetailBill(null)
                fetchBills()
            } else {
                alert(data.error || 'Failed to cancel bill')
            }
        } catch {
            alert('Network error')
        } finally {
            setCancelling(false)
        }
    }

    // ── Export ───────────────────────────────────────────────────────────────────
    const handleExport = () => {
        if (!bills.length) return
        const wb = XLSX.utils.book_new()
        const rows = bills.map(b => ({
            'Bill Number': b.bill_number,
            'Date': fmtDate(b.created_at),
            'Customer Name': b.customer_name || '',
            'Customer Phone': b.customer_phone || '',
            'Item Code': b.item_code || '',
            'Category': b.jewelry_category || '',
            'Purity': b.gold_purity ? `${b.gold_purity}K` : '',
            'Gross Wt (g)': parseFloat(b.gross_weight || 0).toFixed(3),
            'Currency': b.currency,
            'Gold Value': parseFloat(b.gold_value || 0),
            'Making Charges': parseFloat(b.making_charges || 0),
            'Stone Total': parseFloat(b.stone_total || 0),
            'Customs / GST': parseFloat(b.customs_amount || 0),
            'Discount': parseFloat(b.discount_amount || 0),
            'Grand Total': parseFloat(b.grand_total || 0),
            'Status': b.status,
            'Estimate #': b.estimate_number || '',
            'Created By': b.created_by_name || '',
        }))
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Billing History')
        XLSX.writeFile(wb, `Billing_History_${todayStr}.xlsx`)
    }

    // ── Render ───────────────────────────────────────────────────────────────────

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <PageIdentifier pageId={SCREEN_IDS.BILLING.VIEW_BILLS} pageName="Billing History" />

            {/* Header */}
            <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <FileText className="text-amber-600" size={28} />
                        Billing History
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Browse, search and manage all completed bills</p>
                </div>
                <button
                    onClick={handleExport}
                    disabled={!bills.length}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                >
                    <FileDown size={16} /> Export to Excel
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                    <Filter size={15} className="text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">Filters</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">

                    {/* Search */}
                    <div className="lg:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                        <div className="relative">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={e => handleFilterChange('search', e.target.value)}
                                placeholder="Bill#, customer, item code…"
                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                            />
                        </div>
                    </div>

                    {/* Date From */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                        <input type="date" value={filters.startDate}
                            onChange={e => handleFilterChange('startDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                    </div>

                    {/* Date To */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                        <input type="date" value={filters.endDate}
                            onChange={e => handleFilterChange('endDate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                    </div>

                    {/* Currency */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
                        <select value={filters.currency} onChange={e => handleFilterChange('currency', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                            <option value="all">All</option>
                            <option value="INR">INR</option>
                            <option value="USD">USD</option>
                        </select>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                        <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                            <option value="all">All</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                </div>

                {/* Reset row */}
                <div className="flex items-center justify-between mt-3">
                    <button onClick={resetFilters}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                        <RotateCcw size={12} /> Reset Filters
                    </button>
                    <span className="text-xs text-gray-400">
                        {loading ? 'Loading…' : `${bills.length} bill${bills.length !== 1 ? 's' : ''} on this page`}
                    </span>
                </div>
            </div>

            {/* Error banner */}
            {fetchError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                    <XCircle size={16} className="flex-shrink-0" />
                    <span><strong>Error loading bills:</strong> {fetchError}</span>
                </div>
            )}

            {/* Summary Cards */}
            {!loading && bills.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mb-5">
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-xs text-gray-500">Bills (this page)</p>
                        <p className="text-2xl font-bold text-gray-800">{summary.count}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3">
                        <IndianRupee size={20} className="text-orange-500 flex-shrink-0" />
                        <div>
                            <p className="text-xs text-gray-500">INR Total</p>
                            <p className="text-lg font-bold text-orange-600">₹{fmtNum(summary.inr)}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3">
                        <DollarSign size={20} className="text-green-500 flex-shrink-0" />
                        <div>
                            <p className="text-xs text-gray-500">USD Total</p>
                            <p className="text-lg font-bold text-green-600">${fmtNum(summary.usd)}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="text-center py-14">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
                            <p className="mt-2 text-gray-500 text-sm">Loading bills…</p>
                        </div>
                    ) : bills.length === 0 ? (
                        <div className="text-center py-16">
                            <FileText className="mx-auto h-12 w-12 text-gray-200" />
                            <p className="mt-3 text-gray-500 font-medium">No bills found</p>
                            <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                        </div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="w-8 px-3 py-3" />
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Bill #</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Item Code</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Currency</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-600 uppercase tracking-wider">Grand Total</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bills.map(bill => {
                                    const isExpanded = expandedRows.has(bill.id)
                                    const isCancelled = bill.status === 'cancelled'

                                    return (
                                        <React.Fragment key={bill.id}>
                                            {/* Main row */}
                                            <tr
                                                className={`border-b border-gray-100 cursor-pointer transition-colors ${isCancelled ? 'bg-red-50/40 hover:bg-red-50' : isExpanded ? 'bg-amber-50' : 'hover:bg-gray-50'
                                                    }`}
                                                onClick={() => toggleRow(bill.id)}
                                            >
                                                <td className="px-3 py-3 text-gray-400">
                                                    {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                                </td>
                                                <td className="px-4 py-3 font-mono font-semibold text-blue-700">{bill.bill_number}</td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(bill.created_at)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="font-medium text-gray-800">{bill.customer_name}</div>
                                                    {bill.customer_phone && <div className="text-xs text-gray-400">{bill.customer_phone}</div>}
                                                </td>
                                                <td className="px-4 py-3 font-mono text-gray-700">
                                                    {bill.item_count > 1
                                                        ? <span className="font-sans text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{bill.item_count} items</span>
                                                        : (bill.item_code || <span className="text-gray-300">—</span>)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${bill.currency === 'USD' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                                                        }`}>
                                                        {bill.currency === 'USD' ? <DollarSign size={10} /> : <IndianRupee size={10} />}
                                                        {bill.currency}
                                                    </span>
                                                </td>
                                                <td className={`px-4 py-3 text-right font-bold ${isCancelled ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                                                    {currSym(bill.currency)}{fmtNum(bill.grand_total)}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <StatusBadge status={bill.status} />
                                                </td>
                                                <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => setDetailBill(bill)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                                                    >
                                                        <Eye size={12} /> View
                                                    </button>
                                                </td>
                                            </tr>

                                            {/* Expanded detail row */}
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={9} className="px-0 py-0 bg-amber-50/60 border-b border-amber-200">
                                                        <div className="px-8 py-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Category</p>
                                                                <p className="text-gray-700">{bill.jewelry_category || '—'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Purity</p>
                                                                <p className="text-gray-700">{bill.gold_purity ? `${bill.gold_purity}K` : '—'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Gross / Net Wt</p>
                                                                <p className="text-gray-700">
                                                                    {bill.gross_weight ? `${parseFloat(bill.gross_weight).toFixed(2)}g` : '—'} / {bill.net_weight ? `${parseFloat(bill.net_weight).toFixed(2)}g` : '—'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Gold Rate (24K)</p>
                                                                <p className="text-gray-700">{bill.gold_rate_24k ? `₹${fmtNum(bill.gold_rate_24k)}` : '—'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Gold Value</p>
                                                                <p className="text-gray-700">{bill.gold_value ? `${currSym(bill.currency)}${fmtNum(bill.gold_value)}` : '—'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Making + Wastage</p>
                                                                <p className="text-gray-700">
                                                                    {currSym(bill.currency)}{fmtNum((parseFloat(bill.making_charges || 0) + parseFloat(bill.other_charges || 0)))}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Stone Total</p>
                                                                <p className="text-gray-700">{bill.stone_total > 0 ? `${currSym(bill.currency)}${fmtNum(bill.stone_total)}` : '—'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 font-medium mb-0.5">Customs / GST</p>
                                                                <p className="text-gray-700">
                                                                    {bill.customs_amount > 0
                                                                        ? `${currSym(bill.currency)}${fmtNum(bill.customs_amount)} (${bill.customs_percent || 0}%)`
                                                                        : '—'}
                                                                </p>
                                                            </div>
                                                            {bill.estimate_number && (
                                                                <div className="col-span-2 md:col-span-4">
                                                                    <p className="text-xs text-amber-600">📋 Converted from estimate <strong>{bill.estimate_number}</strong></p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {!loading && bills.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                        <button
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            ← Previous
                        </button>
                        <span className="text-xs text-gray-500">
                            Page {page + 1} · Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + bills.length}
                            {totalCount > 0 ? ` of ${totalCount}` : ''}
                        </span>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={bills.length < PAGE_SIZE}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {detailBill && (
                <BillDetailModal
                    bill={detailBill}
                    onClose={() => setDetailBill(null)}
                    onCancel={(bill) => { setDetailBill(null); setCancelTarget(bill) }}
                />
            )}

            {/* Cancel Confirm Modal */}
            {cancelTarget && (
                <CancelModal
                    bill={cancelTarget}
                    onConfirm={handleCancelConfirm}
                    onClose={() => setCancelTarget(null)}
                    cancelling={cancelling}
                />
            )}
        </div>
    )
}

export default BillingHistory
